import React, { useState } from 'react';
import { useNotifications } from '../../contexts/NotificationsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import * as api from '../../services/api';

interface NotificationPopoverProps {
    onClose: () => void;
}

export const NotificationPopover: React.FC<NotificationPopoverProps> = ({ onClose }) => {
    const { user } = useAuth();
    const { showToast } = useToast();
    const { 
        notifications, 
        unreadCount, 
        markAsRead, 
        markAllAsRead, 
        deleteNotification, 
        deleteReadNotifications,
        deleteAllNotifications,
        checkDeadlines
    } = useNotifications();
    const [isChecking, setIsChecking] = useState(false);
    const [isConfirmingDeleteAll, setIsConfirmingDeleteAll] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const readCount = notifications.filter(n => n.is_read).length;

    const handleNotificationClick = (id: string) => {
        markAsRead(id);
        onClose();
    };

    const handleCheckDeadlines = async () => {
        setIsChecking(true);
        try {
            await checkDeadlines();
        } finally {
            setIsChecking(false);
        }
    };

    const handleTestNotification = async () => {
        try {
            if (user) {
                const targetId = user.clientId || user.id;
                await api.createAppNotification(targetId, "Test-Benachrichtigung: Das System funktioniert!", "/");
                showToast("Test-Benachrichtigung gesendet", "info");
            }
        } catch (e) {
            console.error("Test notification failed", e);
        }
    };

    const handleDeleteRead = async () => {
        const count = readCount;
        await deleteReadNotifications();
        showToast(`${count} gelesene ${count === 1 ? 'Mitteilung' : 'Mitteilungen'} gelöscht`, "info");
    };

    const handleDeleteAll = async () => {
        setIsDeleting(true);
        try {
            await deleteAllNotifications();
            setIsConfirmingDeleteAll(false);
            showToast("Alle Benachrichtigungen wurden gelöscht", "info");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleMarkAllRead = async () => {
        await markAllAsRead();
        showToast("Alle Mitteilungen als gelesen markiert", "info");
    };

    return (
        <div className="absolute right-0 mt-2 w-80 md:w-96 z-50">
            <Card className="shadow-2xl border-gray-700 max-h-[80vh] flex flex-col">
                <CardHeader className="flex justify-between items-center py-3 px-4 bg-surface/95 backdrop-blur-sm sticky top-0 z-10 border-b border-gray-700">
                    <div className="flex items-center gap-2">
                        <CardTitle className="text-base font-bold">Benachrichtigungen</CardTitle>
                        {unreadCount > 0 && (
                            <span className="bg-rose-500/20 text-rose-400 border border-rose-500/30 text-[10px] font-mono font-bold px-2 py-0.5 rounded-full">
                                {unreadCount} neu
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <Button 
                            variant="secondary" 
                            size="sm" 
                            className={`!p-1.5 text-xs ${isChecking ? 'animate-spin opacity-100' : 'opacity-60 hover:opacity-100'}`} 
                            onClick={handleCheckDeadlines}
                            title="Fälligkeiten & Aufgaben prüfen"
                            disabled={isChecking}
                        >
                            🔄
                        </Button>
                        <Button 
                            variant="secondary" 
                            size="sm" 
                            className="!p-1.5 text-xs opacity-50 hover:opacity-100" 
                            onClick={handleTestNotification}
                            title="Test-Benachrichtigung senden"
                        >
                            🧪
                        </Button>
                        {notifications.length > 0 && (
                            <>
                                {unreadCount > 0 && (
                                    <Button 
                                        variant="secondary" 
                                        size="sm" 
                                        className="!p-1.5 text-text-secondary hover:text-text-main" 
                                        onClick={handleMarkAllRead} 
                                        title="Alle als gelesen markieren"
                                    >
                                        ✓
                                    </Button>
                                )}
                                <Button 
                                    variant="danger" 
                                    size="sm" 
                                    className="!p-1.5" 
                                    onClick={() => setIsConfirmingDeleteAll(true)} 
                                    title="Alle auf einmal löschen"
                                >
                                    🗑️
                                </Button>
                            </>
                        )}
                        <Button 
                            variant="secondary" 
                            size="sm" 
                            className="!p-1.5 ml-1" 
                            onClick={onClose}
                        >
                            ×
                        </Button>
                    </div>
                </CardHeader>

                <CardContent className="p-0 overflow-y-auto flex-grow max-h-[55vh]">
                    {notifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 flex flex-col items-center">
                            <span className="text-4xl mb-2">😴</span>
                            <p className="text-sm">Keine Benachrichtigungen vorhanden.</p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-800">
                            {notifications.map(notification => {
                                const isOverdue = notification.content.includes('überfällig') || notification.content.includes('Überfällig');
                                const isDueSoon = notification.content.includes('bald fällig') || notification.content.includes('in Kürze fällig');

                                let itemBg = !notification.is_read ? 'bg-primary/5' : '';
                                if (isOverdue) {
                                    itemBg = !notification.is_read ? 'border-l-2 border-l-red-500 bg-red-500/10' : 'border-l-2 border-l-red-500/40 bg-red-500/5';
                                } else if (isDueSoon) {
                                    itemBg = !notification.is_read ? 'border-l-2 border-l-amber-500 bg-amber-500/10' : 'border-l-2 border-l-amber-500/40 bg-amber-500/5';
                                }

                                const dotColor = isOverdue ? 'bg-red-500' : (isDueSoon ? 'bg-amber-400' : 'bg-primary');

                                return (
                                <li 
                                    key={notification.id} 
                                    className={`relative group transition-colors hover:bg-gray-800/50 ${itemBg}`}
                                >
                                    <div className="flex p-3.5 gap-3 items-center">
                                        {!notification.is_read && (
                                            <span className={`w-2 h-2 shrink-0 ${dotColor} rounded-full`} />
                                        )}
                                        <div className="flex-grow min-w-0">
                                            <Link 
                                                to={notification.link} 
                                                onClick={() => handleNotificationClick(notification.id)}
                                                className="block"
                                            >
                                                <p className={`text-xs sm:text-sm leading-snug break-words ${!notification.is_read ? 'font-semibold text-white' : 'text-text-main'}`}>
                                                    {notification.content}
                                                </p>
                                                <p className="text-[11px] text-gray-500 mt-1">
                                                    {new Date(notification.created_at).toLocaleString('de-DE')}
                                                </p>
                                            </Link>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            {!notification.is_read && (
                                                <button 
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        markAsRead(notification.id);
                                                    }} 
                                                    className="p-1.5 rounded-lg text-text-secondary hover:text-primary hover:bg-surface-light active:scale-95 transition-all cursor-pointer" 
                                                    title="Als gelesen markieren"
                                                >
                                                    ✓
                                                </button>
                                            )}
                                            <button 
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    deleteNotification(notification.id);
                                                }} 
                                                className="p-1.5 rounded-lg text-text-secondary hover:text-red-400 hover:bg-red-500/10 active:scale-95 transition-all opacity-80 hover:opacity-100 cursor-pointer" 
                                                title="Löschen"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                </li>
                                );
                            })}
                        </ul>
                    )}
                </CardContent>

                {/* Footer Action Bar */}
                {notifications.length > 0 && (
                    <div className="bg-surface/95 border-t border-gray-800 transition-all">
                        {isConfirmingDeleteAll ? (
                            <div className="p-3 bg-red-950/40 border-t border-red-500/30 flex flex-col sm:flex-row items-center justify-between gap-2.5 animate-in fade-in duration-150">
                                <span className="text-red-300 font-medium text-xs text-center sm:text-left">
                                    Wirklich alle <strong className="text-white">{notifications.length}</strong> Mitteilungen löschen?
                                </span>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setIsConfirmingDeleteAll(false)}
                                        disabled={isDeleting}
                                        className="px-2.5 py-1 text-xs text-text-secondary hover:text-text-main rounded-md hover:bg-surface-light transition-colors cursor-pointer"
                                    >
                                        Abbrechen
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDeleteAll}
                                        disabled={isDeleting}
                                        className="px-3 py-1 bg-red-600 hover:bg-red-500 text-white font-semibold rounded-lg shadow-sm text-xs transition-colors flex items-center gap-1 cursor-pointer"
                                    >
                                        <span>{isDeleting ? 'Löscht...' : 'Ja, alle löschen'}</span>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="py-2.5 px-3.5 flex flex-wrap justify-between items-center text-xs gap-2">
                                <div className="text-text-secondary text-[11px]">
                                    <span>{notifications.length} {notifications.length === 1 ? 'Mitteilung' : 'Mitteilungen'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {readCount > 0 && (
                                        <button
                                            type="button"
                                            onClick={handleDeleteRead}
                                            className="text-text-secondary hover:text-amber-400 font-medium transition-colors flex items-center gap-1 py-1 px-2 rounded-lg hover:bg-surface-light cursor-pointer text-xs"
                                            title="Nur die bereits gelesenen Nachrichten löschen"
                                        >
                                            <span>🧹 Gelesene löschen ({readCount})</span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setIsConfirmingDeleteAll(true)}
                                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 font-medium transition-colors flex items-center gap-1 py-1 px-2.5 rounded-lg border border-red-500/20 hover:border-red-500/40 cursor-pointer text-xs"
                                        title="Alle Benachrichtigungen auf einmal löschen"
                                    >
                                        <span>🗑️ Alle auf einmal löschen</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
};